import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ConfirmDeleteModal from "../components/common/ConfirmDeleteModal/ConfirmDeleteModal";
import {
  CheckCircle,
  Search,
  Edit2,
  Trash2,
  Plus,
  ChevronsUpDown,
} from "lucide-react";
import InnerHeader from "../components/common/InnerHeader/InnerHeader";
import InnerFooter from "../components/common/InnerFooter/InnerFooter";
import AssetsSidebar from "../components/common/AssetsSidebar/AssetsSidebar";
import "./Dashboard.css";
import "./Categories.css";
import "./styles/pages-common.css";

// ─── Initial Data ─────────────────────────────────────────────────────────────
const INITIAL_CATEGORIES = [
  { id: 1, name: "Cartridge" },
  { id: 2, name: "Fan" },
  { id: 3, name: "LED Controller" },
  { id: 4, name: "LED MS Cabinet" },
  { id: 5, name: "LED Receiving Card" },
];

const EMPTY_FORM = { category: "" };

// ─── Component ────────────────────────────────────────────────────────────────
export const Categories = ({ setView }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [alert, setAlert] = useState(null);
  const [categories, setCategories] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [activeTopNav, setActiveTopNav] = useState("Asset");

  // ── Form state ───────────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null); // null = Add mode
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);

  // ── Fetch user ──────────────────────────────────────────────────────────────
  const fetchUserDetails = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) return null;
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/user/AlluserDetails/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("user");
          return null; // Force re-login
        }
        throw new Error("Failed");
      }
      const data = await response.json();
      if (data.status && data.data?.user) {
        const userData = data.data.user;
        if (userData.profile_picture && !userData.picture)
          userData.picture = userData.profile_picture;
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
        return userData;
      }
      throw new Error("Invalid data");
    } catch {
      const stored = localStorage.getItem("user");
      if (stored) {
        const u = JSON.parse(stored);
        setUser(u);
        return u;
      }
      return null;
    }
  };

  const fetchCategories = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      if (!accessToken) return;
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/category`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("accessToken");
          if (setView) setView("signin");
          return;
        }
        throw new Error("Failed to fetch categories");
      }
      const data = await response.json();
      const mapCategory = (c) => ({
        id: String(c.id ?? c.category_id ?? c.pk ?? ""),
        name: c.category_name || c.name || "",
        status: c.status === "deactive" || c.status === false ? false : true,
      });

      if (data && data.status && Array.isArray(data.data)) {
        setCategories(data.data.map(mapCategory));
      } else if (Array.isArray(data)) {
        setCategories(data.map(mapCategory));
      } else if (data && Array.isArray(data.data)) {
        setCategories(data.data.map(mapCategory));
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    let isMounted = true;

    fetchUserDetails().then(userData => {
      if (!userData && isMounted) {
        setAlert({ type: "error", message: "Error loading user information" });
        setTimeout(() => setView && setView("signin"), 500);
      }
    });

    // Fetch categories in the background
    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, [setView]);

  // ── Filtered + sorted list ────────────────────────────────────────────────
  const displayed = (categories || [])
    .filter(
      (c) =>
        !searchQuery ||
        c.name.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .sort((a, b) =>
      sortDir === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name),
    );

  // ── Open Add form ─────────────────────────────────────────────────────────
  const openAddForm = () => {
    setEditingCategory(null);
    setFormData(EMPTY_FORM);
    setFormError("");
    setShowForm(true);
  };

  // ── Open Edit form ────────────────────────────────────────────────────────
  const openEditForm = (cat) => {
    setEditingCategory(cat);
    setFormData({ category: cat.name });
    setFormError("");
    setShowForm(true);
  };

  const toggleStatus = async (idx, e) => {
    if (e) e.stopPropagation();
    const cat = categories[idx];
    if (!cat) return;

    const newStatus = !cat.status;
    const statusString = newStatus ? "active" : "deactive";
    const accessToken = localStorage.getItem("accessToken");

    // Optimistic update
    setCategories((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, status: newStatus } : c)),
    );

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/category/update-status/${cat.id}/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: statusString, id: cat.id }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update status");
      }

      setAlert({
        type: "success",
        message: `Category "${cat.name}" status updated to ${statusString}.`,
      });
      setTimeout(() => setAlert(null), 3000);
    } catch (error) {
      console.error(error);
      setAlert({ type: "error", message: "Failed to update category status." });
      setTimeout(() => setAlert(null), 3000);
      // Revert local state
      setCategories((prev) =>
        prev.map((c, i) => (i === idx ? { ...c, status: cat.status } : c)),
      );
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = () => {
    setCategories((prev) => prev.filter((c) => c.id !== pendingDelete));
    setAlert({ type: "success", message: "Category deleted successfully." });
    setTimeout(() => setAlert(null), 3000);
    setPendingDelete(null);
  };

  // ── Form field change ─────────────────────────────────────────────────────
  const handleFormChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setFormError("");
  };

  // ── Validate & submit ─────────────────────────────────────────────────────
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category.trim()) {
      setFormError("Category name is required.");
      return;
    }

    const accessToken = localStorage.getItem("accessToken");

    if (editingCategory) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/category/update/${editingCategory.id}/`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              category_name: formData.category,
              last_updated_by: user?.username || user?.user_id || user?.id,
            }),
          }
        );

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem("accessToken");
            if (setView) setView("signin");
            return;
          }
          throw new Error("Failed to update category");
        }

        setCategories((prev) =>
          prev.map((c) =>
            c.id === editingCategory.id ? { ...c, name: formData.category } : c,
          ),
        );
        setAlert({
          type: "success",
          message: `Category "${formData.category}" updated successfully!`,
        });
        setTimeout(() => setAlert(null), 3000);
        setFormData(EMPTY_FORM);
        setEditingCategory(null);
        setShowForm(false);
      } catch (error) {
        setFormError(error.message || "Error updating category");
      }
    } else {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/category/create/`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              category_name: formData.category,
              //created_by: user?.username || "admin",
              created_by: user?.username || user?.user_id || user?.id,
              last_updated_by: user?.username || user?.user_id || user?.id,

            }),
          },
        );

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem("accessToken");
            if (setView) setView("signin");
            return;
          }
          throw new Error("Failed to create category");
        }

        await fetchCategories();

        setAlert({
          type: "success",
          message: `Category "${formData.category}" added successfully!`,
        });
        setTimeout(() => setAlert(null), 3000);
        setFormData(EMPTY_FORM);
        setEditingCategory(null);
        setShowForm(false);
      } catch (error) {
        setFormError(error.message || "Error creating category");
      }
    }
  };

  // ── Cancel ────────────────────────────────────────────────────────────────
  const handleCancel = () => {
    setFormData(EMPTY_FORM);
    setEditingCategory(null);
    setFormError("");
    setShowForm(false);
  };

  const isEditMode = Boolean(editingCategory);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="assets-root"
    >
      {/* ══ 1. MAIN HEADER ══════════════════════════════════════════════════════ */}
      <InnerHeader
        user={user}
        setView={setView}
        onProfilePhotoSuccess={async () => {
          setTimeout(async () => {
            await fetchUserDetails();
          }, 500);
          setAlert({
            type: "success",
            message: "Profile picture updated successfully!",
          });
          setTimeout(() => setAlert(null), 3000);
        }}
      />

      {/* ══ 2. SECONDARY BAR ════════════════════════════════════════════════════ */}
      <div className="assets-secondary-bar">
        <div className="assets-title-wrap">
          <div className="assets-plus-icon">
            <Plus size={13} />
          </div>
          <span className="assets-page-title">ASSETS</span>
        </div>
        <div className="assets-top-nav">
          {/* Navigation buttons commented out as requested - preserved for future use
          {["Home", "Dashboard", "Analytics", "Export Reports", "Asset", "Supplier", "Pole Design", "Maintenance"]
            .filter(label => {
              if (label === 'Home') return true;
              const role = user?.role;
              const normRole = role ? role.replace(/[-_ ]/g, '').toLowerCase() : '';
              if (label === 'Analytics' && normRole !== 'superadmin') return false;
              if (normRole === 'technicalsupport') return true;
              if (normRole === 'superadmin') return true;
              if (normRole === 'government') return label === 'Dashboard';
              if (normRole === 'maintenance' || normRole === 'research') {
                return label === 'Dashboard' || label === 'Maintenance';
              }
              if (normRole === 'assetmanagement') {
                return label !== 'Maintenance' && label !== 'Export Reports';
              }
              return true;
            })
            .map((label) => (
              <button
                key={label}
                className={`assets-top-nav-btn${activeTopNav === label ? " active" : ""}`}
                disabled={label === 'Export Reports'}
                style={label === 'Export Reports' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                onClick={() => {
                  setActiveTopNav(label);
                  if (label === "Home") setView && setView("home");
                  else if (label === "Dashboard") setView && setView("dashboard");
                  else if (label === "Analytics") setView && setView("analytics");
                  else if (label === "Asset") setView && setView("assets-list");
                  else if (label === "Supplier") setView && setView("supplier-list");
                  else if (label === "Pole Design") setView && setView("pole-designs");
                  else if (label === "Maintenance") setView && setView("maintenance");
                }}
              >
                {label}
              </button>
            ))}
          */}
          <button
            className="assets-top-nav-btn"
            onClick={() => setView && setView("home")}
            title="Back to Home"
          >
            ← Home
          </button>
        </div>
      </div>

      {/* ══ 3. BODY ═════════════════════════════════════════════════════════════ */}
      <div className="assets-body">
        {/* Sidebar — Categories is active */}
        <AssetsSidebar setView={setView} activeItem="Categories" />

        {/* Main content */}
        <div className="assets-main">
          {/* Alert */}
          {alert && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`assets-alert ${alert.type}`}
            >
              <CheckCircle size={18} />
              <span className="alert-message-flex">{alert.message}</span>
              <button
                className="assets-alert-close"
                onClick={() => setAlert(null)}
              >
                ×
              </button>
            </motion.div>
          )}

          {/* ── ADD / EDIT FORM ──────────────────────────────────────────────── */}
          {showForm ? (
            <motion.div
              key="cat-form"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="site-form-card"
            >
              <div className="site-form-header">
                <h2 className="site-form-title">
                  {isEditMode ? "Edit Category" : "Add New Categories"}
                </h2>
                <p className="site-form-subtitle">
                  {isEditMode
                    ? "Update the category name below."
                    : "Add a category you're working with."}
                </p>
              </div>

              <form
                className="site-form"
                onSubmit={handleFormSubmit}
                noValidate
              >
                {/* Category name — only field */}
                <input
                  className={`site-form-input${formError ? " error" : ""}`}
                  type="text"
                  name="category"
                  placeholder="Category *"
                  value={formData.category}
                  onChange={handleFormChange}
                  autoFocus
                />
                {formError && <p className="site-form-error">{formError}</p>}

                {/* Actions */}
                <div className="site-form-actions">
                  <button type="submit" className="site-form-btn-add">
                    {isEditMode ? "Update" : "Add"}
                  </button>
                  <button
                    type="button"
                    className="site-form-btn-cancel"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          ) : (
            /* ── CATEGORIES TABLE ───────────────────────────────────────────── */
            <div className="assets-card cat-card">
              {/* Toolbar */}
              <div className="cat-toolbar">
                {/* Search */}
                <div className="assets-search-wrap">
                  <Search size={14} color="#64748b" />
                  <input
                    className="assets-search-input"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                {/* Add button */}
                <button className="loc-add-btn" onClick={openAddForm}>
                  Add New Category
                </button>
              </div>

              {/* Table */}
              <div className="assets-table-wrap">
                <table className="assets-table cat-table">
                  <thead>
                    <tr>
                      <th
                        className="assets-th sortable cat-th-name"
                        onClick={() =>
                          setSortDir((d) => (d === "asc" ? "desc" : "asc"))
                        }
                      >
                        Category
                        <ChevronsUpDown size={13} className="loc-sort-icon" />
                      </th>
                      <th className="assets-th cat-th-action">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories === null ? (
                      [...Array(5)].map((_, i) => (
                        <tr key={`skeleton-${i}`}>
                          <td colSpan={2} className="assets-td">
                            <div className="skeleton-loader" style={{ height: "24px", margin: "4px 0" }}>
                              <div className="skeleton-bar" style={{ height: "100%", width: "100%" }}></div>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : displayed.length === 0 ? (
                      <tr>
                        <td
                          colSpan={2}
                          className="assets-td assets-table-empty"
                        >
                          No categories found.
                        </td>
                      </tr>
                    ) : (
                      displayed.map((cat, idx) => (
                        <tr
                          key={cat.id}
                          className={
                            idx % 2 === 0
                              ? "assets-table-row-even"
                              : "assets-table-row-odd"
                          }
                        >
                          <td className="assets-td">{cat.name}</td>
                          <td className="assets-td cat-td-action">
                            <span className="assets-action-group">
                              <button
                                className={`al-toggle-btn ${cat.status ? "on" : ""}`}
                                onClick={(e) =>
                                  toggleStatus(categories.indexOf(cat), e)
                                }
                                title={cat.status ? "Active" : "Inactive"}
                              >
                                <span className="al-toggle-thumb" />
                              </button>
                              <button
                                className="assets-action-btn edit"
                                title="Edit"
                                onClick={() => openEditForm(cat)}
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                className="assets-action-btn delete"
                                title="Delete"
                                onClick={() => setPendingDelete(cat.id)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ══ 4. FOOTER ═══════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {pendingDelete && (
          <ConfirmDeleteModal
            message="Are you sure you want to delete this category? This action cannot be undone."
            onConfirm={handleDelete}
            onCancel={() => setPendingDelete(null)}
          />
        )}
      </AnimatePresence>

      <InnerFooter />
    </motion.div>
  );
};

export default Categories;
